import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { H3, H5 } from "../../../../AbstractElements";
import axios from "axios";

interface UtilizationData {
  name: string;
  BilledHours: number;
  ExpectedHours: string;
  percentage: number;
}

interface ClientUtilizationData {
  name: string;
  client: string;
  BilledHours: number;
  ExpectedHours: number;
  percentage: number | null;
}

type View = "resource" | "client";

const UtilizationReport = () => {
  const [data, setData] = useState<UtilizationData[] | null>(null);
  const [view, setView] = useState<View>("resource");
  const [clientData, setClientData] = useState<ClientUtilizationData[] | null>(null);
  const [clientSort, setClientSort] = useState<{
    key: keyof ClientUtilizationData;
    direction: "asc" | "desc";
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof UtilizationData;
    direction: "asc" | "desc";
  } | null>(null);
  const [selectedMonth, setSelectedMonth] = useState("Current"); // Dropdown state
  const [monthOptions, setMonthOptions] = useState<string[]>([]); // Dropdown options
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      let params = {};

      // Set parameters based on dropdown selection
      if (selectedMonth === "Current") {
        params = {}; // No parameters for 'Current'
      } else {
        const [month, year] = selectedMonth.split("-");
        params = { month: parseInt(month), year: parseInt(year) }; // Set month and year
      }

      if (view === "client") {
        const response = await axios.get("/utilization-by-client", { params });
        setClientData(response.data);
        setClientSort(null);
      } else {
        const response = await axios.get("/utilization", { params });
        setData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, view]);

  // Generate month options for dropdown (rolling last 12 months, cross-year)
  useEffect(() => {
    const now = new Date();

    // Start with "Current" as the default option
    const options: string[] = ["Current"];

    // Include the last 12 months including the current month
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = d.getMonth() + 1; // 1-based
      const year = d.getFullYear();
      options.push(`${month}-${year}`);
    }
    // Optional: remove the duplicate month-year that matches "Current"
    // (If you want "Current" + explicit month list without redundancy)
    // const deduped = Array.from(new Set(options));
    setMonthOptions(options);
  }, []);

  const sortData = (key: keyof UtilizationData) => {
    if (!data) return;

    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    const sortedData = [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setData(sortedData);
    setSortConfig({ key, direction });
  };

  const sortClientData = (key: keyof ClientUtilizationData) => {
    if (!clientData) return;
    const direction: "asc" | "desc" =
      clientSort?.key === key && clientSort.direction === "asc" ? "desc" : "asc";
    const sign = direction === "asc" ? 1 : -1;
    const sorted = [...clientData].sort((a, b) => {
      const av = a[key] ?? -1;
      const bv = b[key] ?? -1;
      return av < bv ? -sign : av > bv ? sign : 0;
    });
    setClientData(sorted);
    setClientSort({ key, direction });
  };

  const getCurrentMonth = () => {
    if (selectedMonth === "Current") {
      const date = new Date();
      return date.toLocaleString("default", { month: "long" });
    } else {
      return monthNames[parseInt(selectedMonth.split("-")[0]) - 1];
    }
  };

  if (loading) {
    return <div>Loading Utilization Report...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const rows: { BilledHours: number; ExpectedHours: string | number }[] | null =
    view === "client" ? clientData : data;
  const totalBilledHours =
    rows?.reduce((sum, item) => sum + Number(item.BilledHours), 0) || 0;
  const totalExpectedHours =
    rows?.reduce(
      (sum, item) => sum + parseFloat(String(item.ExpectedHours || "0")),
      0
    ) || 0;
  const totalUtilization =
    totalExpectedHours > 0
      ? ((totalBilledHours / totalExpectedHours) * 100).toFixed(1)
      : "0.0";

  return (
    <Col xs='12'>
      <style>{`
        .utilization-list {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }
        .utilization-list th, .utilization-list td {
          padding: 12px 15px;
          border: 1px solid #ddd;
          cursor: pointer;
        }
        .utilization-list th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        .utilization-list .even-row {
          background-color: #f9f9f9;
        }
        .utilization-list .odd-row {
          background-color: #fff;
        }
        .utilization-list tr:hover {
          background-color: #f1f1f1;
        }
      `}</style>
      <Row>
        <Col xs='12'>
          <H3 className='font-primary'>
            Utilization Report - {getCurrentMonth()}
          </H3>
        </Col>
      </Row>
      <Row>
        <Col xs='12' className='mb-3'>
          <div className='btn-group' role='group'>
            <button
              type='button'
              className={`btn ${view === "resource" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setView("resource")}
            >
              By Resource
            </button>
            <button
              type='button'
              className={`btn ${view === "client" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setView("client")}
            >
              By Client
            </button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          <H5 className='font-primary'>Select Month and Year</H5>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {monthOptions.map((option, index) => (
              <option key={index} value={option}>
                {option === "Current" ? "Current" : option}
              </option>
            ))}
          </select>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          <H5 className='font-primary'>Total</H5>
          <p>Total Billed Hours: {Math.round(totalBilledHours * 100) / 100}</p>
          <p>Total Expected Hours: {Math.round(totalExpectedHours * 10) / 10}</p>
          <p>Total Utilization: {totalUtilization}%</p>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          {view === "client" ? (
          <>
          <p>
            Expected hours at a client are 8 hours per business day (2 for
            part-time) while the placement is active, split evenly between
            placements that overlap on the same day. Business days with no
            active placement show as "No active placement", so each
            resource's rows add up to their By Resource expected hours.
          </p>
          <table className='utilization-list'>
            <thead>
              <tr>
                <th onClick={() => sortClientData("name")}>Resource</th>
                <th onClick={() => sortClientData("client")}>Client</th>
                <th onClick={() => sortClientData("BilledHours")}>Hours</th>
                <th onClick={() => sortClientData("ExpectedHours")}>Expected Hours</th>
                <th onClick={() => sortClientData("percentage")}>Utilization</th>
              </tr>
            </thead>
            <tbody>
              {clientData?.map((item, index) => (
                <tr
                  key={`${item.name}-${item.client}`}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  <td>{item.name}</td>
                  <td>{item.client}</td>
                  <td>{item.BilledHours}</td>
                  <td>{item.ExpectedHours}</td>
                  <td>{item.percentage === null ? "n/a" : `${item.percentage}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
          ) : (
          <table className='utilization-list'>
            <thead>
              <tr>
                <th onClick={() => sortData("name")}>Name</th>
                <th onClick={() => sortData("BilledHours")}>Billed Hours</th>
                <th>Expected Hours</th>
                <th onClick={() => sortData("percentage")}>Utilization</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  <td>{item.name}</td>
                  <td>{item.BilledHours}</td>
                  <td>{item.ExpectedHours}</td>
                  <td>{item.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default UtilizationReport;
